import { useDevice } from "@root/misc/device";
import I18n from "i18n-js"
import React, { useMemo } from "react"
import Slider, {Settings as SliderSettings} from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useWindowSize } from "@root/misc/useWindowSize";
import _ from "lodash";
// @ts-ignore
import { maxDesktopContentWidth } from "@styles/_export"
import useCountdown from "@root/misc/useCountdown";
import Button from "../Button";

const XSlider = Slider as any // otherwise type error in react

const flagImgContext = require.context("@images/flags", true, /.*\.svg/)
const flagSrc = (countryCode: string) => {
  try {
    return flagImgContext(`./${countryCode}.svg`)
  } catch(err) {
    return ""
  }
}
// round_summary.state_upcoming
// round_summary.btn_review_team

const SkewedBadge = ({ text, variant = "black" }: { text: string, variant?: "black" | "red" }) => {
  return <div className={`roundSummary__skewedBadge roundSummary__skewedBadge--${variant}`}>
    <div className="roundSummary__skewedBadge__bg" />
    <div className="roundSummary__skewedBadge__fg">
      {text}
    </div>
  </div>
}

interface StateInfoProps {
  state: RoundState
  points: number
}

const StateInfo = ({ state, points }: StateInfoProps) => {
  if (state === "live") {
    return <SkewedBadge variant="red" text={I18n.t("round_summary.state.live")} />
  }

  if (state === "over") {
    return <>
      <span>{I18n.t("round_summary.state.over")}</span>
      {points && <SkewedBadge text={I18n.t("general.points_with_label", { points })} />}
    </>
  }

  return <span>{I18n.t(`round_summary.state.${state}`)}</span>
}

type RoundState = "open" | "live" | "upcoming" | "over"

interface RoundSummary {
  title: string // "current event", "last event"
  round_title: string // "DALLAS"
  round_subtitle: string // 2025-09-xx | Spain
  points: number
  bets_path: string // "/round/14"
  date: string // localized, e.g "2025-09-20"
  is_current_round: boolean // true
  missing_bets: number // 0
  num_open_events: number // 3
  country_flag?: string // "DE"
  warn_missing_bets: false
  state: RoundState
  countdown_sec?: number
}

const RoundSummaryView: React.FC<{summary: RoundSummary}> = ({summary}) => {
  const className = [
    "roundSummary",
    summary.state === 'open' ? "roundSummary--current" : "",
  ].join(" ");

  return <div className={className}>
    <h3>{summary.round_title}</h3>
    {/* {summary.country_flag && flagSrc(summary.country_flag) !== ""
      ? <div className="roundSummary__flag">
          <div style={{backgroundImage: `url(${flagSrc(summary.country_flag)})`}} />
        </div>
      : <div className="roundSummary__flagPlaceholder" />} */}
    <div className="roundSummary__info">
      <div>{summary.round_subtitle}</div>
      {summary.state && (
        <div className={`roundSummary__state roundSummary__state--${summary.state}`}>
          <StateInfo state={summary.state} points={summary.points} />
        </div>
      )}
    </div>
    <Button
      disabled={summary.state === "upcoming"}
      href={summary.bets_path}
      primary={summary.state === "open"}
      title={roundButtonLabel(summary)}
    />
  </div>
}

const roundButtonLabel = (summary: RoundSummary) => {
  if (summary.state === "live" || summary.state === "over") {
    return I18n.t("round_summary.btn_review_team")
  }

  return I18n.t("round_summary.btn_set_bets")
}

export const pluralizeDays = (d: string) => {
  const days = parseInt(d)

  return days === 1
    ? I18n.t("general.countdown_short_day")
    : I18n.t("general.countdown_short_days")
}

export const countdownSegmentsToComp = (segments: {d: string, hh: string, mm: string, ss: string}) => {
  const {d, hh, mm, ss} = segments
  return <div className="roundSummaries__countdown">
      <div className="roundSummaries__countdown__bg" />
      <div className="roundSummaries__countdown__fg">
        {`${d}`}
        <span className="roundSummaries__countdown__days">
          {pluralizeDays(d)}
        </span>
        {` ${hh}:${mm}`}
      </div>
    </div>
}

export const CountdownText: React.FC<{initialSec: number}> = ({initialSec}) => {
  const segments = useCountdown({secondsLeft: initialSec})
  return countdownSegmentsToComp(segments)
}

const RoundSummaries: React.FC<{summaries: {table: RoundSummary}[]}> = ({summaries}) => {
  const {isPhone, isDesktopUp, isDesktopLargeUp} = useDevice()
  const {width} = useWindowSize()
  const initialRoundIndex = useMemo(() => {
    let index = _.findIndex(summaries, (s) => s.table.state === 'live'); // first 'live'
    if (index === -1) { index = _.findIndex(summaries, (s) => s.table.state === 'open'); } // or first 'open'
    if (index === -1) { index = _.findLastIndex(summaries, (s) => s.table.state === 'over'); } // or *last* 'over'
    return index === -1 ? 0 : index;
  }, [summaries])


  const sliderSettings: SliderSettings = {
    dots: true,
    variableWidth: true,
    infinite: false,
    arrows: false,
    centerMode: true,
    initialSlide: initialRoundIndex,
  }

  const slideWidth = isPhone
    ? `${Math.min(width - (summaries.length === 1 ? 0 : 100), 400)}px`
    : `${Math.min(width, parseInt(maxDesktopContentWidth)) / (summaries.length > 0 && summaries.length < 3 ? summaries.length : 3) - 8}px` // tablet + desktop

  return <div className="roundSummaries__block">
    <div className="layout__container">
      <h2 className="roundSummaries__title">{I18n.t("dash.next_events.title")}</h2>
    </div>
    <div className="roundSummaries">
      <XSlider {...sliderSettings}>
        {_.map(summaries, (s, index) => {
          return <div key={index} style={{width: slideWidth, position: "relative"}}>
            <div className="roundSummary__holder">
              <RoundSummaryView summary={s.table} />
            </div>
          </div>
        })}
      </XSlider>
    </div>
  </div>
}

export default RoundSummaries
